import { motion } from "framer-motion";
import { EASE } from "@/components/Reveal";

/* ============================== primitives ============================== */

export const GoldDot = ({ cx, cy, r = 4, delay = 0, reduce }) => (
  <motion.circle
    cx={cx}
    cy={cy}
    r={r}
    fill="#E7CE8B"
    initial={reduce ? {} : { opacity: 0, scale: 0.3 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, ease: EASE, delay }}
    style={{ transformOrigin: `${cx}px ${cy}px` }}
  />
);

export const DrawLine = ({ x1, y1, x2, y2, delay = 0, reduce, stroke = "rgba(231,206,139,0.5)" }) => (
  <motion.line
    x1={x1}
    y1={y1}
    x2={x2}
    y2={y2}
    stroke={stroke}
    strokeWidth="1"
    initial={reduce ? {} : { pathLength: 0 }}
    whileInView={{ pathLength: 1 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 1, ease: EASE, delay }}
  />
);

export const FadeG = ({ children, delay = 0, reduce, from = { x: 0, y: 0 } }) => (
  <motion.g
    initial={reduce ? {} : { opacity: 0, x: from.x, y: from.y }}
    whileInView={{ opacity: 1, x: 0, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, ease: EASE, delay }}
  >
    {children}
  </motion.g>
);

export const SvgLabel = ({ x, y, children, anchor = "middle", gold = false, size = 10.5, delay = 0, reduce }) => (
  <motion.text
    x={x}
    y={y}
    fontSize={size}
    letterSpacing="2.5"
    textAnchor={anchor}
    fill={gold ? "#E7CE8B" : "#A79E8E"}
    style={{ textTransform: "uppercase", fontFamily: "'Fraunces Variable', Georgia, serif" }}
    initial={reduce ? {} : { opacity: 0 }}
    whileInView={{ opacity: 1 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, ease: EASE, delay }}
  >
    {children}
  </motion.text>
);

/* A slow pulse travelling back and forth through a set of points */
export const TravelDot = ({ points, duration = 9, reduce, r = 3 }) => {
  if (reduce || points.length < 2) {
    return <circle cx={points[0][0]} cy={points[0][1]} r={r} fill="#F0DDA6" opacity="0.9" />;
  }
  const fwd = points;
  const back = points.slice(1, -1).reverse();
  const route = [...fwd, ...back];
  return (
    <motion.circle
      r={r}
      fill="#F0DDA6"
      initial={{ cx: route[0][0], cy: route[0][1], opacity: 0 }}
      animate={{ cx: route.map((p) => p[0]), cy: route.map((p) => p[1]), opacity: 1 }}
      transition={{
        cx: { duration, repeat: Infinity, ease: "easeInOut" },
        cy: { duration, repeat: Infinity, ease: "easeInOut" },
        opacity: { duration: 1, delay: 1.2 },
      }}
      style={{ filter: "drop-shadow(0 0 6px rgba(231,206,139,0.8))" }}
    />
  );
};

const spread = (n, from, to) => Array.from({ length: n }, (_, i) => (n === 1 ? (from + to) / 2 : from + (i * (to - from)) / (n - 1)));

/* ============================== flow diagrams ============================== */

/* Horizontal sequence: Trigger → Process → Decision → Action → Result */
export const FlowH = ({ steps, underlay, reduce }) => {
  const xs = spread(steps.length, 70, 400);
  const y = 42;
  return (
    <svg viewBox="0 0 470 160" className="w-full" style={{ overflow: "visible" }} aria-hidden="true">
      {xs.slice(0, -1).map((x, i) => (
        <DrawLine key={i} x1={x} y1={y} x2={xs[i + 1]} y2={y} delay={0.4 + i * 0.25} reduce={reduce} stroke="rgba(212,175,55,0.4)" />
      ))}
      {steps.map((s, i) => (
        <g key={s}>
          <motion.circle
            cx={xs[i]}
            cy={y}
            r={i === steps.length - 1 ? 5 : 4}
            fill={i === steps.length - 1 ? "#E7CE8B" : "#0E0D09"}
            stroke={i === steps.length - 1 ? "none" : "rgba(212,175,55,0.55)"}
            strokeWidth="1"
            initial={reduce ? {} : { opacity: 0, scale: 0.3 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, ease: EASE, delay: 0.6 + i * 0.25 }}
            style={{ transformOrigin: `${xs[i]}px ${y}px` }}
          />
          <SvgLabel x={xs[i]} y={i % 2 === 0 ? 82 : 104} gold={i === steps.length - 1} size={9.5} delay={0.8 + i * 0.25} reduce={reduce}>
            {s}
          </SvgLabel>
        </g>
      ))}
      {underlay && (
        <>
          <DrawLine x1="70" y1="128" x2="400" y2="128" delay={1.6} reduce={reduce} stroke="rgba(255,255,255,0.12)" />
          <SvgLabel x="235" y="122" size={8.5} delay={1.9} reduce={reduce}>{underlay}</SvgLabel>
        </>
      )}
      <TravelDot points={xs.map((x) => [x, y])} reduce={reduce} duration={steps.length * 2.4} />
    </svg>
  );
};

/* Vertical sequence with side labels: Lead → Qualify → Follow-Up → … */
export const FlowV = ({ steps, reduce, numbered = false, viewWidth = 340 }) => {
  const ys = spread(steps.length, 44, 416);
  const x = 64;
  return (
    <svg viewBox={`0 0 ${viewWidth} 460`} className="mx-auto w-full max-w-md" aria-hidden="true">
      <DrawLine x1={x} y1={ys[0]} x2={x} y2={ys[ys.length - 1]} delay={0.3} reduce={reduce} stroke="rgba(212,175,55,0.45)" />
      {steps.map((s, i) => (
        <g key={s}>
          <DrawLine x1={x} y1={ys[i]} x2="190" y2={ys[i]} delay={0.6 + i * 0.25} reduce={reduce} stroke="rgba(255,255,255,0.1)" />
          <motion.circle
            cx={x}
            cy={ys[i]}
            r="11"
            fill="none"
            stroke="rgba(212,175,55,0.28)"
            strokeWidth="1"
            initial={reduce ? {} : { opacity: 0, scale: 0.4 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.5 + i * 0.25 }}
            style={{ transformOrigin: `${x}px ${ys[i]}px` }}
          />
          <GoldDot cx={x} cy={ys[i]} r={i === steps.length - 1 ? 5 : 4} delay={0.6 + i * 0.25} reduce={reduce} />
          {numbered && (
            <SvgLabel x="204" y={ys[i] + 4} anchor="start" gold size={9} delay={0.75 + i * 0.25} reduce={reduce}>
              {String(i + 1).padStart(2, "0")}
            </SvgLabel>
          )}
          <SvgLabel x={numbered ? 228 : 208} y={ys[i] + 4} anchor="start" gold={i === steps.length - 1} size={numbered ? 9.5 : 11} delay={0.8 + i * 0.25} reduce={reduce}>
            {s}
          </SvgLabel>
        </g>
      ))}
      <TravelDot points={ys.map((yy) => [x, yy])} reduce={reduce} duration={steps.length * 2.4} />
    </svg>
  );
};

/* Central node with satellites: cost drivers, operations hub */
export const Hub = ({ center, spokes, reduce }) => {
  const pos = [
    [78, 62, "end"],
    [342, 62, "start"],
    [78, 292, "end"],
    [342, 292, "start"],
  ];
  return (
    <svg viewBox="0 0 420 340" className="mx-auto w-full max-w-md" aria-hidden="true">
      {pos.map((p, i) => (
        <DrawLine key={i} x1="210" y1="170" x2={p[0]} y2={p[1]} delay={0.5 + i * 0.2} reduce={reduce} stroke="rgba(255,255,255,0.14)" />
      ))}
      <motion.rect
        x="140"
        y="146"
        width="140"
        height="48"
        rx="4"
        fill="rgba(212,175,55,0.08)"
        stroke="rgba(212,175,55,0.55)"
        strokeWidth="1"
        initial={reduce ? {} : { opacity: 0, scale: 0.85 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
        style={{ transformOrigin: "210px 170px" }}
      />
      <SvgLabel x="210" y="175" gold size={11} delay={0.5} reduce={reduce}>{center}</SvgLabel>
      {spokes.map((s, i) => (
        <g key={s}>
          <GoldDot cx={pos[i][0]} cy={pos[i][1]} r={4} delay={0.7 + i * 0.2} reduce={reduce} />
          <SvgLabel x={pos[i][0] + (pos[i][2] === "end" ? -14 : 14)} y={pos[i][1] + 4} anchor={pos[i][2]} size={9.5} delay={0.9 + i * 0.2} reduce={reduce}>
            {s}
          </SvgLabel>
        </g>
      ))}
      <motion.circle
        cx="210" cy="170" r="72" fill="none" stroke="rgba(212,175,55,0.16)" strokeWidth="1" strokeDasharray="2 6"
        initial={reduce ? {} : { opacity: 0, scale: 0.6 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 1.2, ease: EASE, delay: 1.3 }}
        style={{ transformOrigin: "210px 170px" }}
      />
    </svg>
  );
};

/* Two parallel vertical flows with an outcome beneath each column */
export const DualFlow = ({ left, right, reduce }) => {
  const colL = 92;
  const colR = 348;
  const stepY = (n) => spread(n, 66, 258);
  const col = (cx, cfg, delayBase) => (
    <g>
      <SvgLabel x={cx} y="26" gold size={10.5} delay={delayBase} reduce={reduce}>{cfg.title}</SvgLabel>
      <DrawLine x1={cx} y1="44" x2={cx} y2={258} delay={delayBase + 0.2} reduce={reduce} stroke="rgba(212,175,55,0.35)" />
      {cfg.steps.map((s, i) => {
        const y = stepY(cfg.steps.length)[i];
        return (
          <g key={s}>
            <GoldDot cx={cx} cy={y} r={3.5} delay={delayBase + 0.4 + i * 0.18} reduce={reduce} />
            <SvgLabel x={cx + (i % 2 === 0 ? 14 : -14)} y={y + 4} anchor={i % 2 === 0 ? "start" : "end"} size={9} delay={delayBase + 0.55 + i * 0.18} reduce={reduce}>
              {s}
            </SvgLabel>
          </g>
        );
      })}
      <DrawLine x1={cx} y1={258} x2={cx} y2={316} delay={delayBase + 1.2} reduce={reduce} stroke="rgba(212,175,55,0.45)" />
      <motion.rect
        x={cx - 5} y={311} width="10" height="10" transform={`rotate(45 ${cx} 316)`}
        fill="rgba(212,175,55,0.85)"
        initial={reduce ? {} : { opacity: 0, scale: 0.3 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.5, ease: EASE, delay: delayBase + 1.4 }}
        style={{ transformOrigin: `${cx}px 316px` }}
      />
      <SvgLabel x={cx} y={346} gold size={9.5} delay={delayBase + 1.55} reduce={reduce}>{cfg.result}</SvgLabel>
    </g>
  );
  return (
    <svg viewBox="0 0 440 370" className="mx-auto w-full max-w-md" aria-hidden="true">
      <DrawLine x1="220" y1="40" x2="220" y2="330" delay={0} reduce={reduce} stroke="rgba(255,255,255,0.05)" />
      {col(colL, left, 0.2)}
      {col(colR, right, 0.5)}
      <TravelDot points={stepY(left.steps.length).map((y) => [colL, y])} reduce={reduce} duration={left.steps.length * 2.2} />
      <TravelDot points={stepY(right.steps.length).map((y) => [colR, y])} reduce={reduce} duration={right.steps.length * 2.2} />
    </svg>
  );
};

/* Loop: Build → Operate → Learn → Improve */
export const Cycle = ({ steps, reduce }) => {
  const c = [210, 172];
  const r = 112;
  const pts = steps.map((_, i) => {
    const a = (-90 + i * 90) * (Math.PI / 180);
    return [c[0] + r * Math.cos(a), c[1] + r * Math.sin(a)];
  });
  const labelPos = [[210, 34, "middle"], [344, 176, "start"], [210, 318, "middle"], [76, 176, "end"]];
  return (
    <svg viewBox="0 0 420 340" className="mx-auto w-full max-w-md" aria-hidden="true">
      <motion.circle
        cx={c[0]} cy={c[1]} r={r} fill="none" stroke="rgba(212,175,55,0.3)" strokeWidth="1" strokeDasharray="3 7"
        initial={reduce ? {} : { opacity: 0, scale: 0.7 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 1.2, ease: EASE, delay: 0.3 }}
        style={{ transformOrigin: `${c[0]}px ${c[1]}px` }}
      />
      {pts.map((p, i) => (
        <g key={i}>
          <GoldDot cx={p[0]} cy={p[1]} r={4.5} delay={0.5 + i * 0.25} reduce={reduce} />
          <SvgLabel x={labelPos[i][0]} y={labelPos[i][1]} anchor={labelPos[i][2]} gold={i === 1} size={10.5} delay={0.7 + i * 0.25} reduce={reduce}>
            {steps[i]}
          </SvgLabel>
        </g>
      ))}
      <TravelDot points={pts} reduce={reduce} duration={steps.length * 2.6} />
    </svg>
  );
};

/* Two paths diverging: business growth vs the fixed tool */
export const Diverge = ({ a, b, reduce }) => (
  <svg viewBox="0 0 440 240" className="w-full" aria-hidden="true">
    <motion.polyline
      points="40,196 180,148 400,58"
      fill="none" stroke="rgba(212,175,55,0.55)" strokeWidth="1.2"
      initial={reduce ? {} : { pathLength: 0 }}
      whileInView={{ pathLength: 1 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 1.8, ease: EASE, delay: 0.2 }}
    />
    <motion.polyline
      points="40,196 180,182 400,152"
      fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="1"
      initial={reduce ? {} : { pathLength: 0 }}
      whileInView={{ pathLength: 1 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 1.8, ease: EASE, delay: 0.5 }}
    />
    <DrawLine x1="400" y1="64" x2="400" y2="146" delay={1.9} reduce={reduce} stroke="rgba(255,255,255,0.15)" />
    <GoldDot cx={40} cy={196} r={4} delay={0.2} reduce={reduce} />
    <GoldDot cx={400} cy={58} r={5} delay={1.6} reduce={reduce} />
    <circle cx="400" cy="152" r="4" fill="rgba(255,255,255,0.35)" />
    <SvgLabel x="392" y="46" anchor="end" gold size={10.5} delay={1.8} reduce={reduce}>{a}</SvgLabel>
    <SvgLabel x="392" y="168" anchor="end" size={10.5} delay={2} reduce={reduce}>{b}</SvgLabel>
  </svg>
);

/* Scattered sheets converging into one system */
export const Fragment = ({ toLabel, reduce }) => {
  const sheets = [[36, 52], [86, 122], [40, 192], [118, 74], [126, 176], [74, 252]];
  const target = [300, 152];
  return (
    <svg viewBox="0 0 460 300" className="mx-auto w-full max-w-md" aria-hidden="true">
      {sheets.map((s, i) => (
        <DrawLine key={`l${i}`} x1={s[0] + 13} y1={s[1] + 10} x2={target[0]} y2={target[1]} delay={0.8 + i * 0.15} reduce={reduce} stroke="rgba(212,175,55,0.25)" />
      ))}
      {sheets.map((s, i) => (
        <FadeG key={`s${i}`} delay={0.2 + i * 0.12} reduce={reduce} from={{ x: (i % 3) * 8 - 8, y: (i % 2) * 10 - 5 }}>
          <rect x={s[0]} y={s[1]} width="26" height="20" rx="2" fill="rgba(255,255,255,0.03)" stroke={i === 2 ? "rgba(212,175,55,0.5)" : "rgba(255,255,255,0.22)"} strokeWidth="1" />
          <line x1={s[0] + 5} y1={s[1] + 7} x2={s[0] + 21} y2={s[1] + 7} stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
          <line x1={s[0] + 5} y1={s[1] + 13} x2={s[0] + 16} y2={s[1] + 13} stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
        </FadeG>
      ))}
      <motion.rect
        x="300" y="112" width="132" height="80" rx="6"
        fill="rgba(212,175,55,0.08)" stroke="rgba(212,175,55,0.55)" strokeWidth="1"
        initial={reduce ? {} : { opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.9, ease: EASE, delay: 1.6 }}
        style={{ transformOrigin: "366px 152px" }}
      />
      <SvgLabel x="366" y="148" gold size={10.5} delay={1.9} reduce={reduce}>{toLabel.split(" ")[0]}</SvgLabel>
      <SvgLabel x="366" y="166" gold size={10.5} delay={2} reduce={reduce}>{toLabel.split(" ").slice(1).join(" ") || "System"}</SvgLabel>
      <TravelDot points={[[62, 62], [99, 132], [130, 186], [210, 164], [300, 152]]} reduce={reduce} duration={10} />
    </svg>
  );
};

/* Requirement → modules → system architecture map */
export const Architecture = ({ root, modules, result, reduce }) => {
  const ys = spread(modules.length, 42, 298);
  return (
    <svg viewBox="0 0 480 340" className="mx-auto w-full max-w-lg" aria-hidden="true">
      {ys.map((y, i) => (
        <DrawLine key={`a${i}`} x1="138" y1="171" x2="252" y2={y} delay={0.5 + i * 0.15} reduce={reduce} stroke="rgba(255,255,255,0.12)" />
      ))}
      {ys.map((y, i) => (
        <DrawLine key={`b${i}`} x1="252" y1={y} x2="358" y2={171} delay={1.1 + i * 0.15} reduce={reduce} stroke="rgba(212,175,55,0.3)" />
      ))}
      <FadeG delay={0.2} reduce={reduce}>
        <rect x="16" y="145" width="122" height="52" rx="4" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.25)" strokeWidth="1" />
      </FadeG>
      {root.split(" ").length > 1 ? (
        <>
          <SvgLabel x="77" y="166" size={9} delay={0.5} reduce={reduce}>{root.split(" ").slice(0, -1).join(" ")}</SvgLabel>
          <SvgLabel x="77" y="184" size={9} delay={0.6} reduce={reduce}>{root.split(" ").slice(-1)}</SvgLabel>
        </>
      ) : (
        <SvgLabel x="77" y="176" size={9.5} delay={0.5} reduce={reduce}>{root}</SvgLabel>
      )}
      {modules.map((m, i) => (
        <g key={m}>
          <GoldDot cx={252} cy={ys[i]} r={4} delay={0.8 + i * 0.15} reduce={reduce} />
          <SvgLabel x={268} y={ys[i] + 4} anchor="start" size={9.5} delay={1 + i * 0.15} reduce={reduce}>{m}</SvgLabel>
        </g>
      ))}
      <motion.rect
        x="358" y="139" width="106" height="64" rx="4"
        fill="rgba(212,175,55,0.09)" stroke="rgba(212,175,55,0.55)" strokeWidth="1"
        initial={reduce ? {} : { opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.9, ease: EASE, delay: 1.8 }}
        style={{ transformOrigin: "411px 171px" }}
      />
      {result.split(" ").length > 1 ? (
        <>
          <SvgLabel x="411" y="166" gold size={10} delay={2} reduce={reduce}>{result.split(" ").slice(0, -1).join(" ")}</SvgLabel>
          <SvgLabel x="411" y="184" gold size={10} delay={2.1} reduce={reduce}>{result.split(" ").slice(-1)}</SvgLabel>
        </>
      ) : (
        <SvgLabel x="411" y="176" gold size={10} delay={2} reduce={reduce}>{result}</SvgLabel>
      )}
      <TravelDot points={[[138, 171], [252, ys[2] ?? ys[0]], [358, 171]]} reduce={reduce} duration={7} />
    </svg>
  );
};

/* Layered premium stack with a spine: Content → Conversion */
export const Stack = ({ steps, reduce }) => {
  const ys = spread(steps.length, 44, 416);
  return (
    <svg viewBox="0 0 400 460" className="mx-auto w-full max-w-sm" aria-hidden="true">
      <DrawLine x1="58" y1={ys[0]} x2="58" y2={ys[ys.length - 1]} delay={0.3} reduce={reduce} stroke="rgba(212,175,55,0.45)" />
      {steps.map((s, i) => (
        <g key={s}>
          <FadeG delay={0.5 + i * 0.22} reduce={reduce} from={{ x: 26 }}>
            <rect x="90" y={ys[i] - 17} width="170" height="34" rx="2" fill="rgba(255,255,255,0.025)" stroke={i === steps.length - 1 ? "rgba(212,175,55,0.5)" : "rgba(255,255,255,0.1)"} strokeWidth="1" />
          </FadeG>
          <DrawLine x1="58" y1={ys[i]} x2="90" y2={ys[i]} delay={0.6 + i * 0.22} reduce={reduce} stroke="rgba(212,175,55,0.4)" />
          <GoldDot cx={58} cy={ys[i]} r={i === steps.length - 1 ? 5 : 4} delay={0.6 + i * 0.22} reduce={reduce} />
          <SvgLabel x="282" y={ys[i] + 4} anchor="start" gold={i === steps.length - 1} size={10.5} delay={0.8 + i * 0.22} reduce={reduce}>
            {s}
          </SvgLabel>
        </g>
      ))}
      <motion.circle
        cx="58" cy={ys[ys.length - 1]} r="11" fill="rgba(231,206,139,0.3)"
        initial={{ opacity: 0 }}
        animate={reduce ? { opacity: 0.4 } : { opacity: [0.2, 0.55, 0.2] }}
        transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />
      <TravelDot points={ys.map((y) => [58, y])} reduce={reduce} duration={steps.length * 2.4} />
    </svg>
  );
};

/* Three interlocked roles: CRM / Workflow / Automation */
export const Triangle = ({ nodes, subs, reduce }) => {
  const pts = [[210, 64], [92, 268], [328, 268]];
  return (
    <svg viewBox="0 0 420 340" className="mx-auto w-full max-w-md" aria-hidden="true">
      {[[0, 1], [1, 2], [2, 0]].map(([a, b], i) => (
        <DrawLine key={i} x1={pts[a][0]} y1={pts[a][1]} x2={pts[b][0]} y2={pts[b][1]} delay={0.4 + i * 0.3} reduce={reduce} stroke="rgba(212,175,55,0.4)" />
      ))}
      {pts.map((p, i) => (
        <g key={i}>
          <motion.circle
            cx={p[0]} cy={p[1]} r="30" fill="rgba(212,175,55,0.06)" stroke="rgba(212,175,55,0.35)" strokeWidth="1"
            initial={reduce ? {} : { opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.6 + i * 0.25 }}
            style={{ transformOrigin: `${p[0]}px ${p[1]}px` }}
          />
          <GoldDot cx={p[0]} cy={p[1]} r={4} delay={0.8 + i * 0.25} reduce={reduce} />
          <SvgLabel x={p[0]} y={p[1] + (i === 0 ? -40 : 48)} gold={i === 0} size={10.5} delay={1 + i * 0.25} reduce={reduce}>
            {nodes[i]}
          </SvgLabel>
          {subs && (
            <SvgLabel x={p[0]} y={p[1] + (i === 0 ? -26 : 62)} size={8.5} delay={1.15 + i * 0.25} reduce={reduce}>
              {subs[i]}
            </SvgLabel>
          )}
        </g>
      ))}
      <TravelDot points={[...pts, pts[0]]} reduce={reduce} duration={9} />
    </svg>
  );
};

/* Index-hero knowledge network */
export const KnowledgeSystem = ({ reduce }) => {
  const nodes = [
    { label: "Strategy", p: [220, 66] },
    { label: "Digital Experience", p: [80, 182] },
    { label: "CRM", p: [360, 182] },
    { label: "Automation", p: [80, 330] },
    { label: "Software", p: [360, 330] },
    { label: "Operations", p: [220, 414] },
  ];
  const links = [[0, 1], [0, 2], [1, 2], [1, 3], [2, 4], [3, 4], [3, 5], [4, 5], [0, 5], [1, 4], [2, 3]];
  const route = [0, 2, 4, 5, 3, 1, 0].map((i) => nodes[i].p);
  return (
    <div className="relative" aria-hidden="true">
      <motion.div
        className="pointer-events-none absolute left-1/2 top-1/2 h-[70%] w-[80%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#D4AF37]/[0.07] blur-[100px]"
        animate={reduce ? {} : { opacity: [0.5, 0.9, 0.5] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />
      <svg viewBox="0 0 440 460" className="relative mx-auto w-full max-w-md">
        {[73, 146, 219, 292, 365].map((v) => (
          <g key={v} stroke="rgba(255,255,255,0.035)" strokeWidth="1">
            <line x1={v} y1="0" x2={v} y2="460" />
            <line x1="0" y1={v} x2="440" y2={v} />
          </g>
        ))}
        {links.map(([a, b], i) => (
          <DrawLine key={i} x1={nodes[a].p[0]} y1={nodes[a].p[1]} x2={nodes[b].p[0]} y2={nodes[b].p[1]} delay={0.4 + i * 0.14} reduce={reduce} stroke={i > 8 ? "rgba(255,255,255,0.08)" : "rgba(212,175,55,0.3)"} />
        ))}
        <motion.rect
          x="214" y="234" width="12" height="12" transform="rotate(45 220 240)"
          fill="rgba(212,175,55,0.5)"
          initial={reduce ? {} : { opacity: 0, scale: 0.3 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, ease: EASE, delay: 2.2 }}
          style={{ transformOrigin: "220px 240px" }}
        />
        <motion.rect
          x="211" y="231" width="18" height="18" transform="rotate(45 220 240)"
          fill="none" stroke="rgba(212,175,55,0.35)" strokeWidth="1"
          initial={{ opacity: 0 }}
          animate={reduce ? { opacity: 0.5 } : { opacity: [0.25, 0.6, 0.25] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 2.4 }}
          style={{ transformOrigin: "220px 240px" }}
        />
        {nodes.map((n, i) => (
          <g key={n.label}>
            <motion.circle
              cx={n.p[0]} cy={n.p[1]} r="12" fill="none" stroke="rgba(212,175,55,0.28)" strokeWidth="1"
              initial={reduce ? {} : { opacity: 0, scale: 0.4 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.9 + i * 0.18 }}
              style={{ transformOrigin: `${n.p[0]}px ${n.p[1]}px` }}
            />
            <GoldDot cx={n.p[0]} cy={n.p[1]} r={4} delay={1 + i * 0.18} reduce={reduce} />
            <SvgLabel x={n.p[0]} y={n.p[1] + (i === 0 ? -22 : 32)} gold={i === 0} size={9.5} delay={1.2 + i * 0.18} reduce={reduce}>
              {n.label}
            </SvgLabel>
          </g>
        ))}
        <TravelDot points={route} reduce={reduce} duration={16} r={3.5} />
      </svg>
    </div>
  );
};

/* Small deterministic node accent for cards */
export const MiniMesh = ({ seed = "x", reduce, className = "" }) => {
  const hash = [...seed].reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 7);
  const rnd = (i) => (((hash + i * 7919) * 2654435761) >>> 0) % 1000 / 1000;
  const pts = [0, 1, 2, 3].map((i) => [10 + rnd(i * 2) * 100, 8 + rnd(i * 2 + 1) * 48]);
  const gold = hash % 4;
  return (
    <svg viewBox="0 0 120 64" className={className} aria-hidden="true">
      {[[0, 1], [1, 2], [2, 3], [0, 2]].map(([a, b], i) => (
        <line key={i} x1={pts[a][0]} y1={pts[a][1]} x2={pts[b][0]} y2={pts[b][1]} stroke="rgba(212,175,55,0.28)" strokeWidth="1" />
      ))}
      {pts.map((p, i) =>
        i === gold ? (
          <motion.circle
            key={i} cx={p[0]} cy={p[1]} r="3" fill="#E7CE8B"
            animate={reduce ? {} : { opacity: [0.55, 1, 0.55] }}
            transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: (hash % 9) * 0.4 }}
          />
        ) : (
          <circle key={i} cx={p[0]} cy={p[1]} r="2.4" fill="rgba(255,255,255,0.28)" />
        )
      )}
    </svg>
  );
};

/* Panel shell for diagrams */
export const DiagramPanel = ({ caption, testid, children, className = "" }) => (
  <div
    data-testid={testid}
    className={`relative overflow-hidden rounded-2xl border border-white/10 bg-[#0E0D09] p-6 sm:p-8 ${className}`}
  >
    <div
      className="pointer-events-none absolute inset-0 bg-[radial-gradient(70%_60%_at_50%_100%,rgba(212,175,55,0.07),transparent_70%)]"
      aria-hidden="true"
    />
    <div className="relative">{children}</div>
    {caption && (
      <div className="relative mt-5 flex items-center justify-between gap-4 border-t border-white/[0.06] pt-4">
        <span className="text-[10px] font-medium uppercase tracking-[0.3em] text-stone-500">Fig.</span>
        <span className="text-right text-[10px] font-normal uppercase tracking-[0.25em] text-stone-400">{caption}</span>
      </div>
    )}
  </div>
);

/* Resolve a design config into a diagram */
export const ArticleVisual = ({ config, reduce }) => {
  if (!config) return null;
  switch (config.type) {
    case "flow-h":
      return <FlowH steps={config.steps} underlay={config.underlay} reduce={reduce} />;
    case "flow-v":
      return <FlowV steps={config.steps} reduce={reduce} />;
    case "hub":
      return <Hub center={config.center} spokes={config.spokes} reduce={reduce} />;
    case "dual":
      return <DualFlow left={config.left} right={config.right} reduce={reduce} />;
    case "cycle":
      return <Cycle steps={config.steps} reduce={reduce} />;
    case "diverge":
      return <Diverge a={config.a} b={config.b} reduce={reduce} />;
    case "fragment":
      return <Fragment toLabel={config.toLabel} reduce={reduce} />;
    case "architecture":
      return <Architecture root={config.root} modules={config.modules} result={config.result} reduce={reduce} />;
    case "stack":
      return <Stack steps={config.steps} reduce={reduce} />;
    case "triangle":
      return <Triangle nodes={config.nodes} subs={config.subs} reduce={reduce} />;
    default:
      return null;
  }
};
